import { create } from 'zustand'

export interface LineConversation {
  organizationId: string
  lineUserId: string
  displayName: string | null
  pictureUrl: string | null
  status: 'active' | 'inactive'
  unreadCount: number
  lastMessage: string | null
  lastMessageAt: string | null
  createdAt: string
  updatedAt: string
  /** 紐付け顧客ID（Phase 3） */
  customerId?: string
  /** 担当オペレーターID（Phase 4） */
  assignedOperatorId?: string
  /** 対応ステータス（Phase 4） */
  responseStatus?: 'unhandled' | 'in_progress' | 'done'
  /** メモ（Phase 4） */
  notes?: string
  /** タグ（Phase 4） */
  tags?: string[]
  /** ピン留め日時（設定されていれば上部固定） */
  pinnedAt?: string
}

export type LineMessageType = 'text' | 'sticker' | 'image' | 'video' | 'audio' | 'location' | 'file' | 'contact'

export interface LineMessage {
  organizationId: string
  messageId: string
  lineUserId: string
  direction: 'incoming' | 'outgoing'
  type: LineMessageType
  text?: string
  /** スタンプ情報 */
  stickerPackageId?: string
  stickerId?: string
  stickerResourceType?: string
  /** メディア情報（image/video/audio/file） */
  mediaUrl?: string
  thumbnailUrl?: string
  /** ファイル情報（file タイプ） */
  fileName?: string
  fileSize?: number
  /** 位置情報（location タイプ） */
  latitude?: number
  longitude?: number
  address?: string
  /** 連絡先情報（contact タイプ） */
  contactDisplayName?: string
  contactUserId?: string
  /** 動画の再生時間（秒） */
  duration?: number
  lineMessageId?: string
  operatorId?: string
  createdAt: string
  /** 楽観的更新用: 送信中/失敗 */
  _sendStatus?: 'sending' | 'failed'
  /** 楽観的更新用: 仮ID（API成功後に実IDに差し替え） */
  _tempId?: string
}

interface LineChatStore {
  // State
  conversations: LineConversation[]
  selectedLineUserId: string | null
  messages: LineMessage[]
  conversationsLoading: boolean
  messagesLoading: boolean

  // ページネーション
  conversationsNextCursor: string | null
  conversationsLoadingMore: boolean
  messagesNextCursor: string | null
  messagesLoadingMore: boolean

  // Computed
  totalUnreadCount: number

  // Actions
  setConversations: (convs: LineConversation[]) => void
  appendConversations: (convs: LineConversation[], nextCursor: string | null) => void
  setConversationsNextCursor: (cursor: string | null) => void
  setConversationsLoadingMore: (loading: boolean) => void
  setSelectedLineUserId: (lineUserId: string | null) => void
  setMessages: (msgs: LineMessage[]) => void
  prependMessages: (msgs: LineMessage[], nextCursor: string | null) => void
  setMessagesNextCursor: (cursor: string | null) => void
  setMessagesLoadingMore: (loading: boolean) => void
  setConversationsLoading: (loading: boolean) => void
  setMessagesLoading: (loading: boolean) => void

  addMessage: (msg: LineMessage) => void
  addOptimisticMessage: (msg: LineMessage) => void
  resolveOptimisticMessage: (tempId: string, realMsg: LineMessage) => void
  failOptimisticMessage: (tempId: string) => void
  removeMessage: (messageId: string) => void

  updateConversationLastMessage: (lineUserId: string, text: string, timestamp: string) => void
  incrementUnread: (lineUserId: string) => void
  resetUnread: (lineUserId: string) => void
  addConversation: (conv: LineConversation) => void
  setConversationInactive: (lineUserId: string) => void
  setConversationCustomerId: (lineUserId: string, customerId: string | undefined) => void
  updateConversationFields: (lineUserId: string, fields: Partial<LineConversation>) => void
}

function computeTotalUnread(conversations: LineConversation[]): number {
  let total = 0
  for (const conv of conversations) {
    total += conv.unreadCount
  }
  return total
}

export const useLineChatStore = create<LineChatStore>((set, get) => ({
  // State
  conversations: [],
  selectedLineUserId: null,
  messages: [],
  conversationsLoading: false,
  messagesLoading: false,
  conversationsNextCursor: null,
  conversationsLoadingMore: false,
  messagesNextCursor: null,
  messagesLoadingMore: false,
  totalUnreadCount: 0,

  // Actions
  setConversations: (convs) => set({
    conversations: convs,
    totalUnreadCount: computeTotalUnread(convs),
  }),

  appendConversations: (convs, nextCursor) => {
    const existing = get().conversations
    // 重複排除
    const existingIds = new Set(existing.map(c => c.lineUserId))
    const newConvs = convs.filter(c => !existingIds.has(c.lineUserId))
    const merged = [...existing, ...newConvs]
    set({
      conversations: merged,
      conversationsNextCursor: nextCursor,
      totalUnreadCount: computeTotalUnread(merged),
    })
  },

  setConversationsNextCursor: (cursor) => set({ conversationsNextCursor: cursor }),
  setConversationsLoadingMore: (loading) => set({ conversationsLoadingMore: loading }),

  setSelectedLineUserId: (lineUserId) => {
    set({ selectedLineUserId: lineUserId })
    if (lineUserId) {
      // 選択時に未読リセット
      get().resetUnread(lineUserId)
    }
  },

  setMessages: (msgs) => set({ messages: msgs }),

  prependMessages: (msgs, nextCursor) => {
    const existing = get().messages
    // 重複排除
    const existingIds = new Set(existing.map(m => m.messageId))
    const newMsgs = msgs.filter(m => !existingIds.has(m.messageId))
    set({
      messages: [...newMsgs, ...existing],
      messagesNextCursor: nextCursor,
    })
  },

  setMessagesNextCursor: (cursor) => set({ messagesNextCursor: cursor }),
  setMessagesLoadingMore: (loading) => set({ messagesLoadingMore: loading }),
  setConversationsLoading: (loading) => set({ conversationsLoading: loading }),
  setMessagesLoading: (loading) => set({ messagesLoading: loading }),

  addMessage: (msg) => {
    const { messages, selectedLineUserId } = get()
    // 選択中のトークのメッセージのみ追加
    if (msg.lineUserId !== selectedLineUserId) return
    // 重複チェック
    if (messages.some(m => m.messageId === msg.messageId)) return
    set({ messages: [...messages, msg] })
  },

  addOptimisticMessage: (msg) => {
    set({ messages: [...get().messages, msg] })
  },

  resolveOptimisticMessage: (tempId, realMsg) => {
    set({
      messages: get().messages.map(m =>
        m._tempId === tempId ? { ...realMsg, _sendStatus: undefined, _tempId: undefined } : m
      ),
    })
  },

  failOptimisticMessage: (tempId) => {
    set({
      messages: get().messages.map(m =>
        m._tempId === tempId ? { ...m, _sendStatus: 'failed' } : m
      ),
    })
  },

  removeMessage: (messageId) => {
    set({ messages: get().messages.filter(m => m.messageId !== messageId) })
  },

  updateConversationLastMessage: (lineUserId, text, timestamp) => {
    const convs = get().conversations.map(c =>
      c.lineUserId === lineUserId
        ? { ...c, lastMessage: text, lastMessageAt: timestamp, updatedAt: timestamp }
        : c
    )
    // ピン留め優先 → lastMessageAt 降順
    convs.sort((a, b) => {
      const aPinned = !!a.pinnedAt
      const bPinned = !!b.pinnedAt
      if (aPinned && !bPinned) return -1
      if (!aPinned && bPinned) return 1
      if (aPinned && bPinned) return (b.pinnedAt as string).localeCompare(a.pinnedAt as string)
      const aTime = a.lastMessageAt ?? a.createdAt
      const bTime = b.lastMessageAt ?? b.createdAt
      return bTime.localeCompare(aTime)
    })
    set({ conversations: convs })
  },

  incrementUnread: (lineUserId) => {
    // 選択中のトークなら未読を増やさない
    if (get().selectedLineUserId === lineUserId) return

    const convs = get().conversations
    const exists = convs.some(c => c.lineUserId === lineUserId)

    if (exists) {
      const updated = convs.map(c =>
        c.lineUserId === lineUserId
          ? { ...c, unreadCount: c.unreadCount + 1 }
          : c
      )
      set({ conversations: updated, totalUnreadCount: computeTotalUnread(updated) })
    } else {
      // 会話リスト未ロードでも未読カウントを反映
      set({ totalUnreadCount: get().totalUnreadCount + 1 })
    }
  },

  resetUnread: (lineUserId) => {
    const convs = get().conversations.map(c =>
      c.lineUserId === lineUserId ? { ...c, unreadCount: 0 } : c
    )
    set({ conversations: convs, totalUnreadCount: computeTotalUnread(convs) })
  },

  addConversation: (conv) => {
    const { conversations } = get()
    // 既存チェック
    if (conversations.some(c => c.lineUserId === conv.lineUserId)) {
      // 既に存在する場合は更新（再フォロー）
      const updated = conversations.map(c =>
        c.lineUserId === conv.lineUserId ? { ...c, ...conv, status: 'active' as const } : c
      )
      set({ conversations: updated, totalUnreadCount: computeTotalUnread(updated) })
      return
    }
    const updated = [conv, ...conversations]
    set({ conversations: updated, totalUnreadCount: computeTotalUnread(updated) })
  },

  setConversationInactive: (lineUserId) => {
    const convs = get().conversations.map(c =>
      c.lineUserId === lineUserId ? { ...c, status: 'inactive' as const } : c
    )
    set({ conversations: convs })
  },

  setConversationCustomerId: (lineUserId, customerId) => {
    const convs = get().conversations.map(c =>
      c.lineUserId === lineUserId ? { ...c, customerId } : c
    )
    set({ conversations: convs })
  },

  updateConversationFields: (lineUserId, fields) => {
    const convs = get().conversations.map(c =>
      c.lineUserId === lineUserId ? { ...c, ...fields } : c
    )
    // pinnedAt が変更された場合はソートし直す
    if ('pinnedAt' in fields) {
      convs.sort((a, b) => {
        const aPinned = !!a.pinnedAt
        const bPinned = !!b.pinnedAt
        if (aPinned && !bPinned) return -1
        if (!aPinned && bPinned) return 1
        if (aPinned && bPinned) return (b.pinnedAt as string).localeCompare(a.pinnedAt as string)
        const aTime = a.lastMessageAt ?? a.createdAt
        const bTime = b.lastMessageAt ?? b.createdAt
        return bTime.localeCompare(aTime)
      })
    }
    set({ conversations: convs })
  },
}))
